import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseAppSyncService } from '../base-app-sync.service';
import { getCommercialContact } from 'src/app/graphql/commercial-services/commercial-contacts/get-commercial-contact';
import { queryForCommercialContacts } from 'src/app/graphql/commercial-services/commercial-contacts/query-for-commercial-contacts';
import { createCommercialContact } from 'src/app/graphql/commercial-services/commercial-contacts/create-commercial-contact';
import { updateCommercialContact } from 'src/app/graphql/commercial-services/commercial-contacts/update-commercial-contact';
import { getCommercialContactHistories } from 'src/app/graphql/commercial-services/commercial-contacts/get-commercial-contact-histories';
import { getCommercialJobRoles } from 'src/app/graphql/commercial-services/commercial-contacts/get-commercial-job-roles';
import { deleteCommercialContact } from 'src/app/graphql/commercial-services/commercial-contacts/delete-commercial-contact';
import {
  CommercialContact,
  CommercialContactConnection,
  CommercialContactHistorySummary,
  CommercialJobRole,
  CommercialContactSortOrQuery,
} from 'src/app/model/commercial-services/commercial-contact';
import { CommercialContactUpdateDto } from 'src/app/model/commercial-services/commercial-contact-update-dto';
import { CommercialContactBuilderService } from '../builder/commercial-contact-builder.service';

@Injectable({
  providedIn: 'root',
})
export class CommercialContactApiService extends BaseAppSyncService {
  private _commercialContactBuilder = inject(CommercialContactBuilderService);

  getCommercialContact(
    id: string,
    version?: number
  ): Observable<CommercialContact | null> {
    return this.query(
      getCommercialContact,
      { id, version },
      (i: any) => this._commercialContactBuilder.buildCommercialContact(i),
      'getCommercialContact'
    );
  }

  getCommercialContactHistories(
    id: string
  ): Observable<CommercialContactHistorySummary[]> {
    return this.query(
      getCommercialContactHistories,
      { id },
      (histories: any[]) =>
        histories.map(
          (history): CommercialContactHistorySummary => ({
            ...history,
            updatedAt: history.updatedAt
              ? new Date(history.updatedAt)
              : undefined,
          })
        ),
      'getCommercialContactHistories'
    );
  }

  getCommercialJobRoles(): Observable<CommercialJobRole[]> {
    return this.query(
      getCommercialJobRoles,
      {},
      (roles: CommercialJobRole[]) => roles,
      'getCommercialJobRoles'
    );
  }

  queryForCommercialContacts(
    limit: number,
    page: number,
    query: CommercialContactSortOrQuery,
    sort: CommercialContactSortOrQuery
  ): Observable<CommercialContactConnection> {
    return this.query(
      queryForCommercialContacts,
      { limit, page, query, sort },
      (i: any) =>
        this._commercialContactBuilder.buildCommercialContactConnection(i),
      'queryForCommercialContacts'
    );
  }

  createCommercialContact(
    input: CommercialContactUpdateDto
  ): Observable<CommercialContact | null> {
    return this.mutate(
      createCommercialContact,
      { input },
      (i: any) => this._commercialContactBuilder.buildCommercialContact(i),
      'createCommercialContact'
    );
  }

  updateCommercialContact(
    id: string,
    input: CommercialContactUpdateDto
  ): Observable<CommercialContact | null> {
    return this.mutate(
      updateCommercialContact,
      { id, input },
      (i: any) => this._commercialContactBuilder.buildCommercialContact(i),
      'updateCommercialContact'
    );
  }

  deleteCommercialContact(id: string): Observable<boolean> {
    return this.mutate(
      deleteCommercialContact,
      { id },
      (response: { value: boolean }) => response?.value === true,
      'deleteCommercialContact'
    );
  }
}
